/**
 * HistogramCanvas Component - Canvas 2D rendering of histogram bars
 * Feature: 012-histogram-display
 */

import { useRef, useEffect } from 'react';
import { HistogramData, HistogramMode } from '../../types/histogram';
import { renderHistogram } from './histogramUtils';
import logger from '../../utils/logger';

export interface HistogramCanvasProps {
  /**
   * Histogram data to render
   */
  histogramData: HistogramData | null;

  /**
   * Display mode: 'luminance' or 'rgb'
   */
  mode: HistogramMode;

  /**
   * Canvas width in pixels (default: 256)
   */
  width?: number;

  /**
   * Canvas height in pixels (default: 150)
   */
  height?: number;

  /**
   * Additional CSS class names
   */
  className?: string;
}

/**
 * HistogramCanvas - Renders histogram using Canvas 2D API
 *
 * Performance target: <5ms rendering time for 256-bin histogram
 *
 * @example
 * <HistogramCanvas
 *   histogramData={histogram}
 *   mode="luminance"
 *   width={256}
 *   height={150}
 * />
 */
export function HistogramCanvas({
  histogramData,
  mode,
  width = 256,
  height = 150,
  className = '',
}: HistogramCanvasProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  /**
   * Render histogram whenever data or mode changes
   */
  useEffect(() => {
    const canvas = canvasRef.current;

    logger.debug('HistogramCanvas', '[HistogramCanvas] Render effect triggered:', {
      hasCanvas: !!canvas,
      hasHistogramData: !!histogramData,
      mode,
      maxBinValue: histogramData?.maxBinValue,
    });

    if (!canvas || !histogramData) {
      logger.debug('HistogramCanvas', '[HistogramCanvas] Skipping render - missing canvas or data');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      logger.error('HistogramCanvas', '[HistogramCanvas] Failed to get 2D context');
      return;
    }

    // Render histogram
    const startTime = performance.now();
    renderHistogram(ctx, histogramData, mode, width, height);
    const elapsed = performance.now() - startTime;

    logger.debug('HistogramCanvas', '[HistogramCanvas] Rendered histogram in', elapsed.toFixed(2), 'ms');

    // Log performance warning if rendering exceeds target
    if (elapsed > 5) {
      logger.warn('HistogramCanvas',
        `[HistogramCanvas] Rendering took ${elapsed.toFixed(2)}ms (target: <5ms)`
      );
    }
  }, [histogramData, mode, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={className}
      style={{
        display: 'block',
        width: '100%',
        height: 'auto',
        backgroundColor: '#1a1a1a', // Dark background for better histogram visibility
      }}
    />
  );
}
